// JoinPage.jsx

import React, { useState, useRef } from "react";
import {
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  EuiButton,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiSpacer,
} from "@elastic/eui";
import "@elastic/eui/dist/eui_theme_light.css";
import { PostOptions } from "../utils/RequestUtils";

const JoinPage = ({ isModalVisible, setIsModalVisible }) => {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      // Allow only single digit or empty
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("Text");
    const digits = pasteData.replace(/\D/g, "").split(""); // Extract digits only

    if (digits.length === 0) {
      return; // Do nothing if no digits are found
    }

    const newCode = [...code];
    let currentIndex = event.target.dataset.index
      ? parseInt(event.target.dataset.index)
      : 0;

    for (let i = 0; i < digits.length && currentIndex < 6; i++) {
      newCode[currentIndex] = digits[i];
      currentIndex += 1;
    }

    setCode(newCode);

    // Focus the next empty input or stay on the last one
    if (currentIndex < 6) {
      inputRefs.current[currentIndex].focus();
    } else {
      inputRefs.current[5].focus();
    }
  };

  const handleSubmit = () => {
    const codeString = code.join("");
    const regex = /^\d{6}$/;
    if (regex.test(codeString)) {
      setIsLoading(true);
      fetch(
        "/api/course/join",
        PostOptions({
          invitation_code: codeString,
        })
      )
        .then((response) => {
          return response.json().then((text) => {
            if (!response.ok) {
              setError(text.message);
              setIsLoading(false);
              setCode(["", "", "", "", "", ""]);
              alert(text.message);
              throw new Error(text.message);
            }
            return text;
          });
        })
        .then((data) => {
          setIsLoading(false);
          setError(null);
          setIsModalVisible(false);
        })
        .catch((error) => {
          console.error("There was an error!", error);
          setIsLoading(false);
          setCode(["", "", "", "", "", ""]);
          // setError(error);
        });
    } else {
      setError("Please enter a valid 6-digit verification code.");
    }
  };

  if (!isModalVisible) {
    return null;
  }

  return (
    <EuiModal onClose={() => setIsModalVisible(false)}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>Enter Code to Join</EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiText>
          <p>
            Please enter the 6-digit verification code sent to join the course.
          </p>
        </EuiText>
        <EuiSpacer />
        <EuiFlexGroup gutterSize="s" justifyContent="center">
          {code.map((char, index) => (
            <React.Fragment key={index}>
              {index === 3 && (
                <EuiFlexItem grow={false} style={{ alignSelf: "center" }}>
                  <EuiText>-</EuiText>
                </EuiFlexItem>
              )}
              <EuiFlexItem grow={false}>
                <EuiFieldText
                  maxLength={1}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  value={char}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onPaste={handlePaste}
                  data-index={index}
                  aria-label={`Verification character ${index + 1}`}
                  style={{ width: "3em", textAlign: "center" }}
                  type="text"
                />
              </EuiFlexItem>
            </React.Fragment>
          ))}
        </EuiFlexGroup>
        {error && (
          <EuiText color="danger">
            <p>{error}</p>
          </EuiText>
        )}
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButton
          onClick={handleSubmit}
          fill
          isDisabled={code.some((char) => char === "") || isLoading}
        >
          {isLoading ? "Submitting..." : "Submit"}
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};

export default JoinPage;
